<script setup>
import { computed, ref } from 'vue'
import LanguageSwitcherHorizontal from '@/components/common/LanguageSwitcherHorizontal.vue'
import AuthBrandPanel from '@/components/authentication/AuthBrandPanel.vue'
import AuthBrandLogo from '@/components/authentication/AuthBrandLogo.vue'
import { forgotPassword } from '@/services/api/auth'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/authStore.js'

const { t } = useI18n()
const authStore = useAuthStore()
const loading = ref(false)
const login = ref('')
const message = ref(null)

const recoveryBrandContent = computed(() => ({
    name: t('components.authentication.forgotPassword.brand.name'),
    badge: t('components.authentication.forgotPassword.brand.badge'),
    title: t('components.authentication.forgotPassword.brand.title'),
    accent: t('components.authentication.forgotPassword.brand.accent'),
    description: t('components.authentication.forgotPassword.brand.description'),
    logo: {
        type: 'image',
        src: '/images/static/logos/logo-icon-white.webp',
        alt: t('components.authentication.forgotPassword.brand.name'),
    },
    highlights: [
        {
            icon: 'verified_user',
            title: t('components.authentication.forgotPassword.brand.secureResetTitle'),
            description: t('components.authentication.forgotPassword.brand.secureResetDescription'),
        },
        {
            icon: 'mail',
            title: t('components.authentication.forgotPassword.brand.emailTitle'),
            description: t('components.authentication.forgotPassword.brand.emailDescription'),
        },
        {
            icon: 'shield_lock',
            title: t('components.authentication.forgotPassword.brand.protectedAccountTitle'),
            description: t('components.authentication.forgotPassword.brand.protectedAccountDescription'),
        },
    ],
}))

const handleForgotPassword = async () => {
    loading.value = true
    message.value = null

    try {
        await forgotPassword(login)
        message.value = {
            type: 'success',
            text: t('common.messages.success.sendEmailReset'),
        }
    } catch (error) {
        message.value = {
            type: 'danger',
            text: t('common.messages.errors.generic'),
        }
    } finally {
        loading.value = false
    }
}
</script>

<template>
    <div class="forgot-password-area">
        <AuthBrandPanel :content="recoveryBrandContent" />

        <main class="recovery-panel">
            <section class="recovery-panel__content">
                <AuthBrandLogo
                    :content="recoveryBrandContent"
                    :logo-override="{ type: 'image', src: '/images/static/logos/logo-icon.png', alt: recoveryBrandContent.name }"
                    class="recovery-panel__mobile-logo auth-brand-logo--mobile"
                />

                <div class="recovery-panel__header">
                    <h2>{{ $t('components.authentication.forgotPassword.panelTitle') }}</h2>
                    <p>{{ $t('components.authentication.forgotPassword.panelSubtitle') }}</p>
                </div>

                <div class="recovery-card">
                    <div v-if="message" :class="['recovery-alert', `recovery-alert--${message.type}`]">
                        {{ message.text }}
                    </div>

                    <form class="recovery-form" @submit.prevent="handleForgotPassword">
                        <div class="recovery-form__group">
                            <label for="forgot-login">{{ $t('common.labels.login') }}</label>
                            <div class="recovery-form__field">
                                <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                    <polyline points="22,6 12,13 2,6" />
                                </svg>
                                <input
                                    id="forgot-login"
                                    v-model="login"
                                    type="text"
                                    :placeholder="$t('components.authentication.forgotPassword.loginPlaceholder')"
                                    autocomplete="username"
                                    required
                                >
                            </div>
                        </div>

                        <button class="recovery-form__submit" type="submit" :disabled="loading">
                            <span v-if="loading" class="recovery-form__spinner" aria-hidden="true"></span>
                            <span>{{ loading ? $t('components.authentication.forgotPassword.sending') : $t('components.authentication.forgotPassword.sendInstructions') }}</span>
                            <svg v-if="!loading" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                                <path d="M5 12h14M12 5l7 7-7 7" />
                            </svg>
                        </button>
                    </form>
                </div>

                <p class="recovery-panel__support">
                    {{ $t('components.authentication.forgotPassword.backTo') }}
                    <RouterLink :to="authStore.signInRoute">
                        {{ $t('components.authentication.forgotPassword.signIn') }}
                    </RouterLink>
                </p>

                <LanguageSwitcherHorizontal class="recovery-panel__languages" />
            </section>
        </main>
    </div>
</template>

<style lang="scss" scoped>
.forgot-password-area {
    min-height: 100vh;
    display: flex;
    font-family: Inter, sans-serif;
    background: #f8f9ff;
}

.recovery-panel {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 32px;
    background: #f8f9ff;

    &__content {
        width: 100%;
        max-width: 420px;
    }

    &__header {
        margin-bottom: 32px;
        text-align: center;

        h2 {
            margin: 0 0 8px;
            color: #260944;
            font-size: 28px;
            font-weight: 800;
            line-height: 1.2;
        }

        p {
            margin: 0;
            color: #a9a9c8;
            font-size: 14px;
            line-height: 1.65;
        }
    }

    &__support {
        margin: 20px 0 0;
        color: #a9a9c8;
        font-size: 12px;
        text-align: center;

        a {
            color: #605dff;
            font-weight: 600;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    &__languages {
        margin-top: 18px;
    }

    &__mobile-logo {
        display: none !important;
    }
}

.recovery-card {
    padding: 36px;
    border: 1px solid #eceef2;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 8px 40px rgba(96, 93, 255, 0.1);
}

.recovery-alert {
    margin-bottom: 18px;
    padding: 12px 14px;
    border-radius: 10px;
    font-size: 13px;
    line-height: 1.45;

    &--success {
        border: 1px solid rgba(25, 135, 84, 0.16);
        background: rgba(25, 135, 84, 0.08);
        color: #146c43;
    }

    &--danger {
        border: 1px solid rgba(220, 53, 69, 0.16);
        background: rgba(220, 53, 69, 0.08);
        color: #b42334;
    }
}

.recovery-form {
    display: flex;
    flex-direction: column;
    gap: 20px;

    &__group {
        label {
            display: block;
            margin-bottom: 8px;
            color: #5b5b98;
            font-size: 13px;
            font-weight: 600;
        }
    }

    &__field {
        position: relative;
        color: #a9a9c8;

        > svg {
            position: absolute;
            top: 50%;
            left: 13px;
            transform: translateY(-50%);
            transition: color 0.18s;
            pointer-events: none;
        }

        input {
            width: 100%;
            min-height: 46px;
            padding: 13px 16px 13px 40px;
            border: 1.5px solid #eceef2;
            border-radius: 10px;
            outline: none;
            background: #f8f9ff;
            color: #260944;
            font-family: Inter, sans-serif;
            font-size: 14px;
            transition: border-color 0.18s, background 0.18s, box-shadow 0.18s;

            &::placeholder {
                color: #a9a9c8;
            }

            &:focus {
                border-color: #605dff;
                background: #fff;
                box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.08);
            }
        }

        &:focus-within {
            color: #605dff;
        }
    }

    &__submit {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: 100%;
        margin-top: 4px;
        padding: 15px;
        border: 0;
        border-radius: 10px;
        background: linear-gradient(135deg, #ffd056 0%, #ffe7389e 100%);
        box-shadow: 0 6px 24px rgba(96, 93, 255, 0.38);
        color: #2c2c2cd0;
        font-family: Inter, sans-serif;
        font-size: 15px;
        font-weight: 700;
        cursor: pointer;
        transition: box-shadow 0.2s, transform 0.2s, opacity 0.2s;

        &:hover:not(:disabled) {
            box-shadow: 0 10px 28px rgba(96, 93, 255, 0.42);
            transform: translateY(-1px);
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.72;
        }
    }

    &__spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(44, 44, 44, 0.28);
        border-top-color: #2c2c2c;
        border-radius: 50%;
        animation: recovery-spin 0.8s linear infinite;
    }
}

@keyframes recovery-spin {
    to {
        transform: rotate(360deg);
    }
}

@media only screen and (max-width: 960px) {
    .forgot-password-area {
        flex-direction: column-reverse;
    }

    .recovery-panel {
        padding: 42px 24px;

        &__mobile-logo {
            display: flex !important;
            width: fit-content;
            margin: 0 auto 28px;
            padding: 10px 14px;
            border-radius: 14px;
        }
    }
}

@media only screen and (max-width: 575px) {
    .recovery-panel {
        padding: 34px 16px;

        &__header {
            margin-bottom: 24px;

            h2 {
                font-size: 24px;
            }
        }
    }

    .recovery-card {
        padding: 24px 18px;
        border-radius: 14px;
    }
}
</style>
